import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { contentFor, type Product } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button } from "@/shared/ui/core";
import { AdminGuard, DataTableFrame, Pagination } from "@/features/admin/components";
import { ArchiveProductDialog } from "./components/ArchiveProductDialog";
import { ProductsListBody } from "./components/ProductsListBody";
import { ProductsToolbar } from "./components/ProductsToolbar";
import { useAdminProducts, useArchiveProduct } from "./products.queries";
import { useApiErrorMessage } from "./useApiErrorMessage";
import {
  applyFilterChange,
  DEFAULT_FILTERS,
  hasActiveFilters,
  toQuery,
  type ProductFilters,
} from "./filters";

/** Lo que se espera antes de pedirle al API la búsqueda que se está tecleando. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * El catálogo de productos del panel.
 *
 * `AdminGuard` envuelve la pantalla ENTERA en vez de comprobar el rol
 * dentro: así, cuando alguien no tiene permiso, el componente de abajo
 * ni siquiera se monta y no llega a pedir `/api/admin/products` — una
 * petición que el servidor rechazaría, pero que igualmente aparecería en
 * los registros como un intento de acceso que nadie hizo a propósito.
 */
export function AdminProductsPage() {
  return (
    <AdminGuard>
      <ProductCatalogScreen />
    </AdminGuard>
  );
}

function ProductCatalogScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<ProductFilters>(DEFAULT_FILTERS);
  const [archiveTarget, setArchiveTarget] = useState<Product | null>(null);

  /*
   * La búsqueda se retrasa un cuarto de segundo antes de viajar al API.
   * Sin esto, escribir "colágeno" son ocho peticiones, y la respuesta de
   * "col" puede llegar DESPUÉS de la de "colágeno" y pintar la lista
   * equivocada.
   */
  const [debouncedSearch, setDebouncedSearch] = useState(DEFAULT_FILTERS.search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const query = useMemo(
    () => toQuery({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );

  const products = useAdminProducts(query);
  const archive = useArchiveProduct();
  const archiveError = useApiErrorMessage(archive.error);

  const items = products.data?.items ?? [];
  const total = products.data?.total ?? 0;
  const categories = products.data?.categories ?? [];
  const filtered = hasActiveFilters(filters);

  function changeFilters(patch: Partial<ProductFilters>) {
    setFilters((current) => applyFilterChange(current, patch));
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    try {
      await archive.mutateAsync(archiveTarget.id);
      setArchiveTarget(null);
    } catch {
      // El error se enseña arriba, en su aviso. El diálogo se queda
      // abierto: cerrarlo daría por hecho que funcionó.
    }
  }

  const locale = i18n.language.startsWith("en") ? "en" : "es";
  const archiveTargetName = archiveTarget
    ? contentFor(archiveTarget.translations, locale).name
    : null;

  return (
    <>
      <PageHeader
        // La barra superior ya dice "Productos": repetirlo aquí, con su
        // rótulo "CATÁLOGO" encima, era leer lo mismo dos veces en la misma
        // pantalla. El título sigue en el árbol para el lector de pantalla.
        titleHidden
        title={t("Productos")}
        description={t("{{total}} productos en el catálogo.", { total })}
        actions={
          <Button
            size="sm"
            leftIcon={<Plus />}
            onClick={() => navigate("/admin/products/new")}
          >
            {t("Nuevo producto")}
          </Button>
        }
        // Sin `divider`: la tabla que viene debajo ya tiene su propio borde superior, y dos
        // líneas seguidas se leen como un error de maquetación (Anahí, 26-ago).
      />

      {archive.isError && (
        <Alert
          variant="error"
          className="mb-4"
          title={t("No se pudo archivar el producto")}
          onDismiss={() => archive.reset()}
        >
          {archiveError}
        </Alert>
      )}

      <DataTableFrame aria-busy={products.isFetching}>
        <ProductsToolbar
          filters={filters}
          categories={categories}
          canReset={filtered}
          onChange={changeFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />

        <ProductsListBody
          products={items}
          loading={products.isPending}
          error={products.error}
          filtered={filtered}
          onRetry={() => void products.refetch()}
          onCreate={() => navigate("/admin/products/new")}
          onResetFilters={() => setFilters(DEFAULT_FILTERS)}
          onEdit={(product) => navigate(`/admin/products/${product.id}`)}
          onArchive={(product) => {
            archive.reset();
            setArchiveTarget(product);
          }}
          archivingId={archive.isPending ? (archiveTarget?.id ?? null) : null}
        />

        {total > 0 && (
          <Pagination
            page={filters.page}
            perPage={filters.perPage}
            total={total}
            onPageChange={(page) => changeFilters({ page })}
            onPerPageChange={(perPage) => changeFilters({ perPage, page: 1 })}
          />
        )}
      </DataTableFrame>

      <ArchiveProductDialog
        productName={archiveTargetName}
        loading={archive.isPending}
        onConfirm={() => void confirmArchive()}
        onCancel={() => setArchiveTarget(null)}
      />
    </>
  );
}
